/**
 * Company: SPHMedia
 * Description: ESG Section Layout
 */

import { ReactElement } from "react";
import { Helmet } from "react-helmet-async";
import { AdvertisementTypeEnum } from "@app/types/enums";
import esgLogo from "@assets/verticals/esg-logo.png";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import { useCategoriesFromPath } from "@components/Header/helpers";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import NewsletterSignUpPanel from "@components/Newsletter/Verticals/NewsletterSignUp";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useMostReadArticleList } from "@hooks/useMostReadArticleList";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import BreakingNews from "@pages/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@pages/Section/components/MostPopular/MostPopular";
import Overview from "@pages/Section/components/Overview/Overview";
import type { SectionDefaultProps } from "@pages/Section/types";
import { sectionNavigationItems } from "@util/constant";
import { GoogleAdsSlotFactory } from "@util/helpers";
import cx from "classnames";

export default function Section({
  context: {
    data: { title, overview },
  },
}: SectionDefaultProps): ReactElement {
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting({
      targeting: { key: "page", value: "esg" },
    });
  const { parentCategory, childCategory } = useCategoriesFromPath();

  const categoryName = childCategory
    ? `${parentCategory}/${childCategory}`
    : parentCategory;

  const mostPopular = useMostReadArticleList();
  const breakingNews = useTrimmedBreakingNews();

  return (
    <div className="w-full">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <Advertisement
        rootClassName="py-5"
        adUnitProps={{
          type: AdvertisementTypeEnum.LB1,
          slot: GoogleAdsSlotFactory.lb1(parentCategory),
        }}
      />

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial(parentCategory)}
        {...{ isPrestitialEnabled, topOverlayImpressions, topOverlayValidity }}
      />

      <Header mastheadTitle={categoryName} />

      {parentCategory && sectionNavigationItems?.[parentCategory] ? (
        <div className={cx("flex-none bg-gray-250", "max-lg:hidden")}>
          <Breadcrumb
            rootClassName="m-auto container"
            parentCategory={parentCategory}
            childCategory={childCategory}
            categoryDataMapping={sectionNavigationItems}
          />
        </div>
      ) : null}

      <Container rootClassName="pt-6">
        <Row>
          <Column rootClassName="w-full">
            <NewsletterSignUpPanel
              rootClassName="mb-6 lg:py-0"
              containerClassName="lg:pl-0"
              descriptionComponent={
                <div
                  className={cx(
                    "mb-4 flex items-center justify-center",
                    "lg:mb-0 lg:justify-start"
                  )}
                >
                  <img
                    className="aspect-square w-[48px] lg:w-[110px]"
                    src={esgLogo}
                    alt="ESG"
                    width={110}
                    height={110}
                  />
                  <h4
                    className={cx(
                      "ml-4 font-public-sans text-base font-semibold lg:text-4xl"
                    )}
                  >
                    Get weekly{" "}
                    <span className="text-verticals-esg">{"ESG "}</span>
                    <br className="md:hidden lg:inline xl:hidden" />
                    updates in your inbox
                  </h4>
                </div>
              }
              newsletter={BT_NEWSLETTERS.filter(
                (newsletter) => newsletter.type === NEWSLETTER_TYPE.ESG
              )}
            />
          </Column>

          <Column rootClassName="w-full lg:w-8/12">
            <Overview data={overview} />
          </Column>

          <Column rootClassName="w-full lg:w-4/12">
            <div className="mb-5 lg:h-[800px]">
              <Advertisement
                rootClassName="lg:sticky lg:top-[120px]"
                adUnitProps={{
                  type: AdvertisementTypeEnum.IMU2,
                  slot: GoogleAdsSlotFactory.imu2(parentCategory),
                }}
              />
            </div>

            <BreakingNews
              data={breakingNews}
              rootClassName="max-w-[300px] mx-auto mb-6"
            />

            <MostPopular
              data={mostPopular}
              rootClassName="max-w-[300px] mx-auto"
            />
          </Column>
        </Row>
      </Container>

      <FooterSubscribeRow />
    </div>
  );
}
